import { Save, Wifi, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '@trajectory/ui';
import { useHealth } from '@/hooks/useHealth';
import { useResolvedTheme } from '@/hooks/useTheme';
import { useSaveIndicator, type SaveTone } from '@/hooks/useSaveIndicator';
import { useThrottledPlayhead } from '@/hooks/useThrottledPlayhead';
import { findIndexForTime } from '@/lib/telemetry-series';
import { useMissionStore } from '@/stores/missionStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useTelemetryStore } from '@/stores/telemetryStore';

const SAVE_TONE_COLOR: Record<SaveTone, string> = {
  off: 'text-faint',
  idle: 'text-success',
  pending: 'text-muted',
  saving: 'text-info',
  saved: 'text-success',
  error: 'text-danger',
};

interface LiveSample {
  altitude: number;
  speed: number;
  mass: number;
}

/** Looks up the telemetry series at the current playhead, at the same throttled rate the scrubber updates. */
function useLiveSample(): LiveSample | null {
  const status = useTelemetryStore((state) => state.status);
  const { time } = useThrottledPlayhead();

  if (status !== 'ready') return null;
  const series = useTelemetryStore.getState().series;
  if (!series) return null;

  const index = findIndexForTime(series.t, time);
  if (index < 0) return null;
  return { altitude: series.altitude[index]!, speed: series.speed[index]!, mass: series.mass[index]! };
}

function useClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return now.toUTCString().slice(17, 25);
}

/** Bottom status strip: wall clock, mission, live flight telemetry once a simulation is running, autosave, theme and API connection. */
export function StatusBar() {
  const clock = useClock();
  const health = useHealth();
  const resolvedTheme = useResolvedTheme();
  const saveIndicator = useSaveIndicator();
  const missionName = useMissionStore((state) => state.document?.name ?? null);
  const liveSample = useLiveSample();
  const autosaveEnabled = useSettingsStore((state) => state.autosaveEnabled);
  const setAutosaveEnabled = useSettingsStore((state) => state.setAutosaveEnabled);

  const connected = health.data?.status === 'ok';

  return (
    <div
      className="flex items-center gap-4 border-t border-border bg-surface px-3 text-xs text-muted"
      style={{ height: 'var(--status-bar-height)' }}
    >
      <span className="tabular-nums">{clock} UTC</span>
      <span className="text-faint">·</span>
      <span className="truncate">{missionName ?? 'No mission selected'}</span>

      {liveSample && (
        <>
          <span className="text-faint">·</span>
          <span className="tabular-nums">alt {(liveSample.altitude / 1000).toFixed(1)} km</span>
          <span className="tabular-nums">vel {(liveSample.speed / 1000).toFixed(2)} km/s</span>
          <span className="tabular-nums">mass {(liveSample.mass / 1000).toFixed(1)} t</span>
        </>
      )}

      <div className="flex-1" />

      {/* The indicator has always had an "Autosave off" state and the setting
          has always been persisted, but nothing in the UI could reach the
          setter — so the state was unreachable and edits could not be held
          back from the server. */}
      <button
        type="button"
        onClick={() => setAutosaveEnabled(!autosaveEnabled)}
        aria-pressed={autosaveEnabled}
        title={autosaveEnabled ? 'Turn autosave off' : 'Turn autosave on'}
        className="flex items-center gap-1 rounded-control px-1 hover:bg-surface-sunken hover:text-text"
      >
        <Save className={cn('h-3 w-3', SAVE_TONE_COLOR[saveIndicator.tone])} aria-hidden />
        {saveIndicator.label}
      </button>

      <span className="capitalize">{resolvedTheme} theme</span>

      <span className="flex items-center gap-1" title={health.error instanceof Error ? health.error.message : undefined}>
        {connected ? (
          <Wifi className="h-3 w-3 text-success" aria-hidden />
        ) : (
          <WifiOff className="h-3 w-3 text-danger" aria-hidden />
        )}
        {connected ? `API v${health.data?.version}` : 'API unreachable'}
      </span>
    </div>
  );
}
